// The compareDateRange merge: one row per current-window bucket, carrying its
// comparison-window partner's measures as `compared.*` twins. Extracted
// VERBATIM from queryTheCube (connect.ts) so the pairing semantics — keyed
// matching, latest-year collision pick (the monthly 13-bucket comparison
// window collapsing to 12 keys), comparison buckets without a current partner
// being dropped — are unit-testable against both keying modes:
// getYoyComparisonKey (default calendar-YoY) and getWindowComparisonKey
// (explicit compare windows, offset-from-window-start).
import { type YoyWindow } from "../yoy-comparison"

/**
 * Merges the two sorted compareDateRange windows into one row per
 * current-window bucket. `keyOf` supplies the stable pairing key per row
 * (window-aware — weekly YoY keys shift comparison rows +364d; offset keys
 * anchor each window at its own start). A comparison bucket whose key has no
 * current partner is intentionally dropped; a current bucket without a match
 * simply gets no `compared.*` twins (blank YoY). On key collisions the latest
 * year present wins, so the most recent prior period is the one compared.
 */
export function mergeCompareWindows(
  sortedCurrentData: Array<Record<string, unknown>>,
  sortedComparisonData: Array<Record<string, unknown>>,
  dateKey: string,
  keyOf: (dateStr: string, window: YoyWindow) => string,
): Array<Record<string, unknown>> {
  const groupByIdentifier = (
    items: Array<Record<string, unknown>>,
    window: "current" | "comparison",
  ) => {
    const map = new Map<
      string,
      Array<Record<string, unknown> & { year: number }>
    >()
    items.forEach(item => {
      const year = new Date(item[dateKey] as string).getFullYear()
      const key = keyOf(item[dateKey] as string, window)
      const existing = map.get(key)
      if (existing) {
        existing.push({ ...item, year })
      } else {
        map.set(key, [{ ...item, year }])
      }
    })
    return map
  }

  const currentDataMap = groupByIdentifier(sortedCurrentData, "current")
  const comparisonDataMap = groupByIdentifier(
    sortedComparisonData,
    "comparison",
  )

  const mergedData: Array<Record<string, unknown>> = []

  currentDataMap.forEach((currentItems, key) => {
    const currentItem = currentItems.reduce((latest, current) =>
      current.year > latest.year ? current : latest,
    )

    const comparisonItems = comparisonDataMap.get(key) || []
    const comparisonItem =
      comparisonItems.length > 0
        ? comparisonItems.reduce((latest, current) =>
            current.year > latest.year ? current : latest,
          )
        : null

    const mergedItem: Record<string, unknown> = { ...currentItem }

    if (comparisonItem) {
      for (const compKey of Object.keys(comparisonItem)) {
        // Skip the synthetic `year` field we attached and the date key
        // itself. Exact-match (not substring `includes`) so we don't drop
        // measure names that happen to contain the date prefix.
        if (compKey !== "year" && compKey !== dateKey) {
          mergedItem[`compared.${compKey}`] = comparisonItem[compKey]
        }
      }
      // Carry the comparison bucket's own date (prefixed, so it can't
      // shadow the row's current date) — data-availability warnings date
      // the YoY sub-value with it.
      mergedItem[`compared.${dateKey}`] = comparisonItem[dateKey]
    }

    mergedData.push(mergedItem)
  })

  return mergedData.sort(
    (a, b) =>
      new Date(a[dateKey] as string).getTime() -
      new Date(b[dateKey] as string).getTime(),
  )
}
